/**
 * Booking type constants for CareCredit URL generation
 */
export const BOOKING_TYPES = {
  APPOINTMENT: 'appointment',
  APPOINTMENT_REQUEST: 'appointment_request',
  DOLPHIN_BOOKING_INTENT: 'dolphin_booking_intent',
  PMS_BOOKING_INTENT: 'pms_booking_intent',
}

/**
 * Medium constants for CareCredit URL generation
 */
export const MEDIUM_TYPES = {
  APP: 'app',
  EMAIL: 'email',
}

/**
 * Builds a CareCredit promo URL with booking context
 *
 * @param {Object} options - URL building options
 * @param {string} options.baseUrl - Base CareCredit URL (required)
 * @param {string} options.bookingType - Type of booking (required, one of BOOKING_TYPES)
 * @param {string} options.appointmentId - ID of the appointment or booking intent (required)
 * @param {string} [options.medium='app'] - Medium type (defaults to 'app')
 * @returns {string} Formatted CareCredit URL with query parameters
 * @throws {Error} If required parameters are missing or invalid
 *
 * @example
 * buildCareCreditUrl({
 *   baseUrl: 'https://example.com',
 *   bookingType: BOOKING_TYPES.APPOINTMENT,
 *   appointmentId: '123',
 *   medium: MEDIUM_TYPES.APP
 * })
 * // Returns: 'https://example.com/care_credit/redirect?booking_type=appointment&appointment_id=123&medium=app'
 */
export const buildCareCreditUrl = ({
  baseUrl,
  bookingType,
  appointmentId,
  medium = MEDIUM_TYPES.APP,
}) => {
  // Validate required parameters
  if (!baseUrl || typeof baseUrl !== 'string' || baseUrl.trim() === '') {
    throw new Error('baseUrl is required and must be a non-empty string')
  }

  if (
    !bookingType ||
    typeof bookingType !== 'string' ||
    !Object.values(BOOKING_TYPES).includes(bookingType)
  ) {
    throw new Error(
      `bookingType must be one of: ${Object.values(BOOKING_TYPES).join(', ')}`
    )
  }

  if (
    !appointmentId ||
    typeof appointmentId !== 'string' ||
    appointmentId.trim() === ''
  ) {
    throw new Error('appointmentId is required and must be a non-empty string')
  }

  if (!Object.values(MEDIUM_TYPES).includes(medium)) {
    throw new Error(
      `medium must be one of: ${Object.values(MEDIUM_TYPES).join(', ')}`
    )
  }

  // Normalize baseUrl - remove trailing slash if present
  const normalizedBaseUrl = baseUrl.replace(/\/$/, '')

  // Build query parameters
  const params = new URLSearchParams({
    booking_type: bookingType,
    appointment_id: appointmentId,
    medium,
  })

  return `${normalizedBaseUrl}/care_credit/redirect?${params.toString()}`
}
